// ApprovalDetail (M1 §8): resumo do objeto + hash → SignatureDialog → StepUpDialog em `step_up_required`
// (a decisão do plano exige step-up sempre — Parte 14.2). SoD e hash desatualizado chegam como ProblemAlert.
import { Badge, Button } from '@giyus/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ProblemAlert, SignatureDialog, StepUpDialog } from '../../../components';
import type { SignatureMeaning } from '../../../components/signatureMeanings';
import { useStepUp } from '../../../lib/auth/useStepUp';
import { useDateFormatter } from '../../../lib/dates/useDateFormatter';
import { apiScopePath } from '../../../lib/workflow/itemRoutes';
import type { WorkflowApprovalDetail } from '../api/contract.preview';
import { useDecideApproval } from '../api/queries';

export interface ApprovalDetailProps {
  readonly approval: WorkflowApprovalDetail;
}

export function ApprovalDetail({ approval }: ApprovalDetailProps) {
  const { t } = useTranslation(['workflow', 'common']);
  const dates = useDateFormatter();
  const scopePath = apiScopePath(approval.scope);
  const decide = useDecideApproval();
  const stepUp = useStepUp();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingMeaning, setPendingMeaning] = useState<SignatureMeaning | null>(null);
  const canDecide = approval.allowedActions.includes('decide');

  function decideWith(
    meaning: SignatureMeaning,
    decision: 'Approved' | 'Rejected' | 'Returned' = 'Approved',
  ): void {
    decide.mutate(
      {
        scopePath,
        approvalId: approval.id,
        decision,
        meaning,
        expectedObjectContentHash: approval.approval.objectContentHash,
      },
      {
        onError: (error) => {
          if (stepUp.isStepUpRequired(error)) {
            setPendingMeaning(meaning);
            stepUp.requestStepUp();
          }
        },
      },
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="approval-detail">
      <div>
        <h1 className="text-title font-semibold">{t('approval.detail.title')}</h1>
        <p className="text-body text-muted-foreground">
          {t('approval.detail.actType')}: {approval.approval.actType}
        </p>
        {approval.approval.objectSummary && (
          <p className="mt-1 text-body" data-testid="approval-object-summary">
            {approval.approval.objectSummary}
          </p>
        )}
        <p
          className="mt-1 font-mono text-caption break-all text-muted-foreground"
          data-testid="approval-object-hash"
        >
          {t('approval.detail.objectHash')}: {approval.approval.objectContentHash}
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          <Badge variant="outline">
            {t('approval.detail.step', { step: approval.approval.step })}
          </Badge>
          {approval.approval.requiresSignature && (
            <Badge variant="secondary">{t('approval.detail.requiresSignature')}</Badge>
          )}
        </div>
      </div>

      {decide.isError && !stepUp.isStepUpRequired(decide.error) && (
        <ProblemAlert
          error={decide.error}
          onRetry={() => {
            decide.reset();
          }}
        />
      )}

      {canDecide && (
        <div className="flex gap-2">
          <SignatureDialog
            open={dialogOpen}
            onOpenChange={setDialogOpen}
            summary={approval.approval.objectSummary ?? approval.approval.objectId}
            contentHash={approval.approval.objectContentHash}
            meanings={approval.approval.allowedMeanings}
            onSign={(meaning) => {
              decideWith(meaning);
              setDialogOpen(false);
            }}
          />
          <Button
            data-testid="approval-decide-button"
            onClick={() => {
              setDialogOpen(true);
            }}
          >
            {t('actions.decide')}
          </Button>
        </div>
      )}

      <StepUpDialog
        open={stepUp.open}
        onCancel={stepUp.cancel}
        onConfirm={async () => {
          await stepUp.confirm();
          if (pendingMeaning) {
            decideWith(pendingMeaning);
            setPendingMeaning(null);
          }
        }}
      />

      <section>
        <h2 className="mb-2 text-body font-semibold">{t('approval.detail.history')}</h2>
        {approval.decisions.length === 0 ? (
          <p className="text-body text-muted-foreground">—</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {approval.decisions.map((decision) => (
              <li key={decision.id} className="rounded-card border p-2 text-body">
                <p className="font-medium">
                  {decision.deciderName} — {decision.decision} ({decision.meaning})
                </p>
                {decision.comment && <p>{decision.comment}</p>}
                <p className="text-caption text-muted-foreground">
                  {dates.dateTime(decision.decidedAt)}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
